import { useState } from "react";
import { AlertTriangle, ArrowRight, ChevronRight, Sparkles } from "lucide-react";
import { MarketInsightsPanel } from "../components/dashboard/MarketInsightsPanel";
import { ShouldCostPanel } from "../components/dashboard/ShouldCostPanel";
import { SpendFilterSection } from "../components/dashboard/SpendFilterSection";

const HERO_TABS = ["Who & Risk", "What & Cost", "Where & Geo", "When & Terms"];

const HERO_STATS = [
  ["44%", "top-3 supplier concentration"],
  ["8–17%", "above should-cost on key parts"],
  ["1", "high-risk supplier (Tenneco)"],
];

const CONCENTRATION = [
  { label: "Castrol", pct: 19.4, color: "#1e1b4b" },
  { label: "Magna International", pct: 15.1, color: "#7c3aed" },
  { label: "Autoliv", pct: 9.6, color: "#8b5cf6" },
  { label: "Continental", pct: 9, color: "#a78bfa" },
];


const TABS = ["Supplier Overview", "Dependency & Risk", "Should-Cost & Price", "Market Insights", "Payment & Working Capital"];

const OVERVIEW_KPIS = [
  { label: "Suppliers, normalized", value: "312", sub: "from raw vendor records", color: "#7c3aed" },
  { label: "Top-3 share", value: "44.1%", sub: "Castrol, Magna, Autoliv", color: "#ec4899" },
  { label: "High-risk suppliers", value: "1", sub: "Tenneco · $3.8M", color: "#ec4899" },
  { label: "Avg spend / supplier", value: "$154K", sub: "across 312 suppliers", color: "#d1d5db" },
];

const SUPPLIER_OVERVIEW = [
  { supplier: "Castrol", parent: "BP plc", region: "United Kingdom", spend: "$9.3M", share: "19.4%", conf: "High", credit: "BBB", risk: "Low" },
  { supplier: "Magna International", parent: "Magna International", region: "Canada", spend: "$7.3M", share: "15.1%", conf: "High", credit: "A−", risk: "Low" },
  { supplier: "Autoliv", parent: "Autoliv Inc", region: "United States", spend: "$4.7M", share: "9.6%", conf: "High", credit: "BBB+", risk: "Low" },
  { supplier: "Continental", parent: "Continental AG", region: "Germany", spend: "$4.3M", share: "9%", conf: "High", credit: "BBB", risk: "Low" },
  { supplier: "Tenneco", parent: "Apollo (PE-held)", region: "United States", spend: "$3.8M", share: "7.8%", conf: "High", credit: "B", risk: "High" },
];

const KPIS = [
  { label: "Average DPO", value: "38.4 d", sub: "lags 45-day best practice", color: "#ec4899" },
  { label: "Inventory turnover", value: "5.9x", sub: "~62 days of stock", color: "#7c3aed" },
  { label: "Working capital freed", value: "$1.32M", sub: "at 45-day DPO", color: "#14b8a6" },
  { label: "Discount capture", value: "$0.42M", sub: "2/10 Net 30", color: "#f59e0b" },
];

const PAYMENT_CYCLE = [
  { label: "Net 30–45 days", value: "58.2%", pct: 58.2 },
  { label: "Net 15–30 days", value: "38.6%", pct: 38.6 },
  { label: "Immediate (< 15d)", value: "3.2%", pct: 3.2 },
  { label: "Net 45+ days", value: "0%", pct: 0 },
];

const VENDOR_TERMS = [
  { vendor: "Castrol", terms: "Net 42", spend: "$9.3M" },
  { vendor: "Magna", terms: "Net 30", spend: "$7.3M" },
  { vendor: "Autoliv", terms: "Net 29", spend: "$4.7M" },
  { vendor: "Continental", terms: "Net 40", spend: "$4.3M" },
  { vendor: "Tenneco", terms: "Net 33", spend: "$3.8M" },
];

const LEVERS = [
  { lever: "Extend core terms to 45d", current: "38.4 d DPO", target: "45 d", impact: "$1.32M WC", owner: "Treasury & AP" },
  { lever: "Dynamic discount 2/10 Net 30", current: "low utilization", target: "85% capture", impact: "$0.42M", owner: "Controller" },
  { lever: "Segment terms by risk tier", current: "flat terms", target: "risk-tiered", impact: "protect supply", owner: "Category Mgrs" },
];

function ConcentrationDonut() {
  const cx = 80;
  const cy = 80;
  const r = 60;
  const strokeW = 28;
  const circ = 2 * Math.PI * r;
  const top = CONCENTRATION.reduce((s, c) => s + c.pct, 0);
  const segments = [...CONCENTRATION, { label: "Others", pct: 100 - top, color: "#ede9fe" }];
  let offset = 0;

  return (
    <svg className="sm-donut" width="160" height="160" viewBox="0 0 160 160" role="img" aria-label="Supplier concentration">
      {segments.map((seg) => {
        const dash = (seg.pct / 100) * circ;
        const rotation = (offset / 100) * 360 - 90;
        offset += seg.pct;
        return (
          <circle
            key={seg.label}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={seg.color}
            strokeWidth={strokeW}
            strokeDasharray={`${dash} ${circ - dash}`}
            transform={`rotate(${rotation} ${cx} ${cy})`}
          />
        );
      })}
      <text x={cx} y={cy - 6} textAnchor="middle" fill="#a397b8" fontSize="11">Top-3</text>
      <text x={cx} y={cy + 14} textAnchor="middle" fill="#fff" fontSize="20" fontWeight="700">44.1%</text>
    </svg>
  );
}

const DEP_KPIS = [
  { label: "Top-3 share", value: "44.1%", sub: "concentration risk", color: "#ec1c8c", accent: true },
  { label: "Suppliers >= 15% spend", value: "2", sub: "Castrol, Magna", color: "#ec1c8c", accent: true },
  { label: "High-risk suppliers", value: "1", sub: "Tenneco", color: "#ec1c8c", accent: true },
  { label: "India concentration", value: "41.1%", sub: "geographic + disruption", color: "#f59e0b", accent: false },
];

const DEPENDENCY = [
  { supplier: "Castrol", spend: "$9.3M", share: "19.4%", level: "Very High" },
  { supplier: "Magna International", spend: "$7.3M", share: "15.1%", level: "High" },
  { supplier: "Autoliv", spend: "$4.7M", share: "9.6%", level: "Medium" },
  { supplier: "Continental", spend: "$4.3M", share: "9%", level: "Medium" },
  { supplier: "Tenneco", spend: "$3.8M", share: "7.8%", level: "Medium" },
];

const SEGMENTS = [
  { key: "bottleneck", title: "Bottleneck", sub: "High risk, low spend", rows: [["Bosch", "$2.1M"], ["Mann+Hummel", "$1.5M"]] },
  { key: "strategic", title: "Strategic", sub: "High risk, high spend", rows: [["Tenneco", "$3.8M"], ["Castrol", "$9.3M"]] },
  { key: "noncritical", title: "Non-critical", sub: "Low risk, low spend", rows: [["NSK, Lear", "$2.8M"]] },
  { key: "leverage", title: "Leverage", sub: "Low risk, high spend", rows: [["Magna", "$7.3M"], ["Autoliv", "$4.7M"]] },
];

const GEO = [
  { country: "India", label: "$19.8M · 41.1%", pct: 41.1 },
  { country: "United States", label: "$6.9M · 14.3%", pct: 14.3 },
  { country: "Canada", label: "$5.8M · 12.1%", pct: 12.1 },
  { country: "Germany", label: "$4.6M · 9.6%", pct: 9.6 },
  { country: "Mexico", label: "$3.8M · 7.8%", pct: 7.8 },
];

const ALTERNATES = [
  { supplier: "Castrol", category: "Engine oil & lubricants", alts: "Shell · Valvoline · TotalEnergies", delta: "-4% to -10%", savings: "$0.94M", priority: "P1" },
  { supplier: "Magna International", category: "Chassis · modules", alts: "Lear · Faurecia · Aisin", delta: "-3% to -8%", savings: "$0.58M", priority: "P1" },
  { supplier: "Autoliv", category: "Airbags · seatbelts", alts: "ZF · Joyson · Tokai Rika", delta: "-3% to -6%", savings: "Risk down", priority: "P1" },
  { supplier: "Continental", category: "Tires · brakes", alts: "Michelin · Bridgestone · Goodyear", delta: "-5% to -9%", savings: "$0.31M", priority: "P2" },
  { supplier: "Tenneco", category: "Radiators · exhaust", alts: "Eaton · Magneti Marelli · Faurecia", delta: "-4% to -7%", savings: "$0.26M", priority: "P2" },
];

const SCORECARD = [
  { supplier: "Tenneco", category: "Radiators, exhaust", credit: "B", risk: "High" },
  { supplier: "Bosch", category: "Wipers, pistons", credit: "A", risk: "Moderate" },
  { supplier: "Castrol", category: "Lubricants", credit: "BBB", risk: "Moderate" },
  { supplier: "Mann+Hummel", category: "Filtration", credit: "NR", risk: "Moderate" },
  { supplier: "SKF", category: "Bearings", credit: "A-", risk: "Moderate" },
  { supplier: "Autoliv", category: "Airbags, seatbelts", credit: "BBB+", risk: "Low" },
  { supplier: "Continental", category: "Tires, brakes", credit: "BBB", risk: "Low" },
  { supplier: "Magna International", category: "Chassis, modules", credit: "A-", risk: "Low" },
  { supplier: "Denso", category: "Engine components", credit: "AA-", risk: "Low" },
  { supplier: "Lear", category: "Seating", credit: "BBB", risk: "Low" },
];

const slug = (s: string) => s.toLowerCase().replace(/\s+/g, "");

function DependencyRiskPanel() {
  return (
    <div className="sm-panel">
      <div className="sm-kpi-grid">
        {DEP_KPIS.map(({ label, value, sub, color, accent }) => (
          <div key={label} className="sm-kpi" style={{ borderColor: color }}>
            <p className="sm-kpi__label">{label}</p>
            <p className={`sm-kpi__value${accent ? " sm-kpi__value--accent" : ""}`}>{value}</p>
            <p className="sm-kpi__sub">{sub}</p>
          </div>
        ))}
      </div>

      <div className="si-grid-2">
        <div className="si-card sm-card sm-card--flush">
          <div className="sm-card__head">
            <p className="sm-card__title">Supplier Dependency</p>
            <span className="sm-tag">Click for alternates</span>
          </div>
          <table className="sm-table">
            <thead>
              <tr>
                <th>Supplier</th>
                <th className="sm-table__right">Spend</th>
                <th className="sm-table__right">Share</th>
                <th>Dependency</th>
              </tr>
            </thead>
            <tbody>
              {DEPENDENCY.map(({ supplier, spend, share, level }) => (
                <tr key={supplier}>
                  <td className="sm-table__strong">{supplier}</td>
                  <td className="sm-table__right sm-table__muted">{spend}</td>
                  <td className="sm-table__right sm-table__muted">{share}</td>
                  <td><span className={`sm-risk-badge sm-risk-badge--${slug(level)}`}>{level}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="sm-callout">
            <div className="sm-callout__body">
              <p className="sm-callout__eyebrow">From: Dependency &amp; Risk</p>
              <p className="sm-callout__text">
                <strong>Castrol alone is 19.4% of spend, "very high" dependency.</strong> Reallocating 10–20% to validated
                alternates cuts concentration and improves leverage.
              </p>
            </div>
            <button type="button" className="sm-callout__btn">
              Send to Opportunity
              <ArrowRight size={14} />
            </button>
          </div>
        </div>

        <div className="si-card sm-card">
          <div className="sm-card__head sm-card__head--flat">
            <p className="sm-card__title">Segmentation, Kraljic</p>
            <span className="sm-tag">Risk x Spend</span>
          </div>
          <div className="sm-kraljic">
            {SEGMENTS.map(({ key, title, sub, rows }) => (
              <div key={key} className={`sm-kraljic__cell sm-kraljic__cell--${key}`}>
                <p className="sm-kraljic__title">{title}</p>
                <p className="sm-kraljic__sub">{sub}</p>
                {rows.map(([name, val]) => (
                  <div key={name} className="sm-kraljic__row">
                    <span>{name}</span>
                    <strong>{val}</strong>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <p className="sm-kraljic__axis">Supply risk (vertical) · spend (horizontal)</p>
        </div>
      </div>

      <div className="si-card sm-card">
        <div className="sm-card__head sm-card__head--stacked sm-card__head--flat">
          <div>
            <p className="sm-card__title">Geographic Concentration</p>
            <p className="sm-card__subtitle sm-card__subtitle--muted">Dependency-lens spend by country, with supply-risk flag</p>
          </div>
          <span className="sm-tag">Country risk</span>
        </div>
        <div className="sm-geo">
          {GEO.map(({ country, label, pct }) => (
            <div key={country} className="sm-geo__row">
              <p className="sm-geo__label">{country}</p>
              <div className="sm-geo__track">
                <div className="sm-geo__fill" style={{ width: `${(pct / GEO[0].pct) * 100}%` }} />
              </div>
              <p className="sm-geo__value">{label}</p>
            </div>
          ))}
        </div>
        <p className="sm-card__subtitle sm-geo__note">
          India holds 41.1% of dependency-lens spend, flagged High risk (monsoon flooding, port congestion, tariff
          exposure). Pre-position inventory buffers and qualify alternates outside the region.
        </p>
      </div>

      <div className="si-card sm-card sm-card--flush">
        <div className="sm-card__head sm-card__head--stacked">
          <div>
            <p className="sm-card__title">Alternative Supplier Recommendations</p>
            <p className="sm-card__subtitle">Validated alternates at lower cost for high-dependency suppliers</p>
          </div>
          <span className="sm-tag">Validated alternates</span>
        </div>
        <table className="sm-table">
          <thead>
            <tr>
              <th>Current Supplier</th>
              <th>Category</th>
              <th>Validated Alternatives</th>
              <th className="sm-table__right">Cost Delta</th>
              <th className="sm-table__right">Savings</th>
              <th className="sm-table__right">Priority</th>
            </tr>
          </thead>
          <tbody>
            {ALTERNATES.map(({ supplier, category, alts, delta, savings, priority }) => (
              <tr key={supplier}>
                <td className="sm-table__strong">{supplier}</td>
                <td className="sm-table__muted">{category}</td>
                <td className="op-purple">{alts}</td>
                <td className="sm-table__right sm-delta">{delta}</td>
                <td className="sm-table__right sm-table__accent sm-table__strong">{savings}</td>
                <td className="sm-table__right">
                  <span className={`sm-risk-badge sm-priority sm-priority--${priority.toLowerCase()}`}>{priority}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="sm-callout">
          <div className="sm-callout__body">
            <p className="sm-callout__eyebrow">From: Dependency &amp; Risk</p>
            <p className="sm-callout__text">
              <strong>Each high-dependency supplier has 3+ validated alternates at 3–10% lower cost.</strong> Onboarding two
              alternates each for Castrol, Magna and Continental de-risks more than 60% of spend.
            </p>
          </div>
          <button type="button" className="sm-callout__btn">
            Send to Opportunity
            <ArrowRight size={14} />
          </button>
        </div>
      </div>

      <div className="si-card sm-card sm-card--flush">
        <div className="sm-card__head sm-card__head--stacked">
          <div>
            <p className="sm-card__title">Risk Scorecard</p>
            <p className="sm-card__subtitle sm-card__subtitle--muted">
              Blended from credit, financial trend, sole-source, and geopolitical exposure
            </p>
          </div>
          <span className="sm-tag">Click for mitigation</span>
        </div>
        <table className="sm-table">
          <thead>
            <tr>
              <th>Supplier</th>
              <th>Category</th>
              <th>Credit</th>
              <th>Risk</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {SCORECARD.map(({ supplier, category, credit, risk }) => (
              <tr key={supplier}>
                <td className="sm-table__strong">{supplier}</td>
                <td className="op-purple">{category}</td>
                <td>{credit}</td>
                <td><span className={`sm-risk-badge sm-risk-badge--${risk.toLowerCase()}`}>{risk}</span></td>
                <td className="sm-table__right sm-table__muted"><ChevronRight size={14} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="sm-callout">
          <div className="sm-callout__body">
            <p className="sm-callout__eyebrow">From: Dependency &amp; Risk</p>
            <p className="sm-callout__text">
              <strong>Tenneco is the lone high-risk supplier ($3.76M, B rating, PE-held).</strong> Each supplier carries a
              generated mitigation action, assign owners to convert risk into tracked work.
            </p>
          </div>
          <button type="button" className="sm-callout__btn sm-callout__btn--filled">
            Push actions to Execution
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

export function SupplierMarketView() {
  const [heroTab, setHeroTab] = useState(HERO_TABS[0]);
  const [activeTab, setActiveTab] = useState(TABS[0]);

  return (
    <div className="si-page">
      <div className="sm-header">
        <div>
          <p className="si-header__eyebrow">Spend Insights</p>
          <h2 className="si-header__title">Supplier &amp; Market</h2>
          <p className="si-header__description">
            Continuously generated recommendations across consolidation, leakage, tail spend, and payment optimization.
          </p>
        </div>
        <div className="sm-header__actions">
          <span className="sm-pill">
            <Sparkles size={14} />
            14 active opportunities
          </span>
          <button type="button" className="sm-pill sm-pill--outline">Generate Executive Summary</button>
        </div>
      </div>

      <section className="si-hero sm-hero">
        <div className="sm-hero__main">
          <h3 className="sm-hero__headline">
            You depend on three suppliers for 44% of spend, and you pay 8 to 17% above what your top parts should cost.
          </h3>
          <p className="sm-hero__sub">
            A structured view of who you buy from, how dependent you are, and where the negotiation leverage lives.
          </p>
          <div className="si-hero__buttons">
            {HERO_TABS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setHeroTab(t)}
                className={`si-hero__chip${heroTab === t ? " si-hero__chip--active" : ""}`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="si-hero__stats sm-hero__stats">
            {HERO_STATS.map(([val, label]) => (
              <div key={label}>
                <p className="si-hero__stat-value sm-hero__stat-value">{val}</p>
                <p className="si-hero__stat-label">{label}</p>
              </div>
            ))}
          </div>
          <div className="si-hero__cta-group sm-hero__cta-group">
            <button type="button" className="sm-hero__cta">
              Review should-cost gaps
              <ArrowRight size={14} />
            </button>
            <p className="sm-hero__gap">
              <AlertTriangle size={14} />
              Supplier should-cost gap: <strong>$3.8M</strong>
            </p>
          </div>
          <div className="si-hero__synth">
            <Sparkles size={12} />
            <p className="si-hero__synth-text">AI synthesis · updated 4 min ago</p>
          </div>
        </div>

        <aside className="sm-concentration">
          <p className="sm-concentration__title">Supplier Concentration</p>
          <ConcentrationDonut />
          <div className="sm-concentration__legend">
            {CONCENTRATION.map(({ label, pct, color }) => (
              <div key={label} className="sm-concentration__item">
                <span className="sm-concentration__swatch" style={{ background: color }} />
                <p className="sm-concentration__label">{label}</p>
                <p className="sm-concentration__pct">{pct}%</p>
              </div>
            ))}
          </div>
        </aside>
      </section>

      <SpendFilterSection labelClassName="sm-filter-pill__label" chevronSize={14} savedViewsIcon="bookmark" clearLabel="X Clear" />

      <section>
        <p className="si-section-eyebrow">Deep Dive by Section</p>
        <div className="si-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setActiveTab(t)}
              className={`si-tab sm-tab${activeTab === t ? " si-tab--active" : ""}`}
            >
              {t}
            </button>
          ))}
        </div>

        {activeTab === "Supplier Overview" ? (
          <div className="sm-panel">
            <div className="sm-kpi-grid">
              {OVERVIEW_KPIS.map(({ label, value, sub, color }) => (
                <div key={label} className="sm-kpi sm-kpi--overview" style={{ borderColor: color }}>
                  <p className="sm-kpi__label">{label}</p>
                  <p className="sm-kpi__value">{value}</p>
                  <p className="sm-kpi__sub">{sub}</p>
                </div>
              ))}
            </div>

            <div className="si-card sm-card sm-card--flush">
              <div className="sm-card__head sm-card__head--stacked">
                <div>
                  <p className="sm-card__title">Supplier Overview</p>
                  <p className="sm-card__subtitle">Cleaned name and parent are generated; risk and credit come from web enrichment</p>
                </div>
                <span className="sm-tag">Click a row for profile</span>
              </div>
              <table className="sm-table sm-table--overview">
                <thead>
                  <tr>
                    <th>Supplier</th>
                    <th>Ultimate Parent</th>
                    <th>Region</th>
                    <th className="sm-table__right">Spend</th>
                    <th className="sm-table__right">Share</th>
                    <th>Name Conf.</th>
                    <th className="sm-table__right">Credit</th>
                    <th>Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {SUPPLIER_OVERVIEW.map(({ supplier, parent, region, spend, share, conf, credit, risk }) => (
                    <tr key={supplier}>
                      <td className="sm-table__strong">{supplier}</td>
                      <td className="op-purple">{parent}</td>
                      <td className="sm-table__muted">{region}</td>
                      <td className="sm-table__right sm-table__strong">{spend}</td>
                      <td className="sm-table__right sm-table__muted">{share}</td>
                      <td className="sm-table__muted">
                        <span className="sm-conf-dot" />
                        {conf}
                      </td>
                      <td className={`sm-table__right sm-table__strong${risk === "High" ? " sm-table__accent" : ""}`}>{credit}</td>
                      <td>
                        <span className={`sm-risk-badge sm-risk-badge--${risk.toLowerCase()}`}>{risk}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : activeTab === "Market Insights" ? (
          <MarketInsightsPanel />
        ) : activeTab === "Should-Cost & Price" ? (
          <ShouldCostPanel />
        ) : activeTab === "Dependency & Risk" ? (
          <DependencyRiskPanel />
        ) : activeTab === "Payment & Working Capital" ? (
          <div className="sm-panel">
            <div className="sm-kpi-grid">
              {KPIS.map(({ label, value, sub, color }) => (
                <div key={label} className="sm-kpi" style={{ borderColor: color }}>
                  <p className="sm-kpi__label">{label}</p>
                  <p className="sm-kpi__value">{value}</p>
                  <p className="sm-kpi__sub">{sub}</p>
                </div>
              ))}
            </div>

            <div className="si-grid-2">
              <div className="si-card sm-card">
                <p className="sm-card__title">Spend by Payment Cycle</p>
                <div className="si-bar-list">
                  {PAYMENT_CYCLE.map(({ label, value, pct }) => (
                    <div key={label} className="si-bar-row">
                      <p className="si-bar-row__label sm-bar-label">{label}</p>
                      <div className="si-bar-row__track">
                        <div className="si-bar-row__fill sm-bar-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <p className="si-bar-row__value">{value}</p>
                    </div>
                  ))}
                </div>
                <p className="si-card__footnote">
                  Terms cluster in Net 30–45; no early-pay terms in use, room to extend on stable suppliers.
                </p>
              </div>

              <div className="si-card sm-card sm-card--flush">
                <p className="sm-card__title sm-card__title--padded">Payment Terms by Vendor</p>
                <table className="sm-table">
                  <thead>
                    <tr>
                      <th>Vendor</th>
                      <th>Terms</th>
                      <th className="sm-table__right">Spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {VENDOR_TERMS.map(({ vendor, terms, spend }) => (
                      <tr key={vendor}>
                        <td className="sm-table__strong">{vendor}</td>
                        <td className="sm-table__muted">{terms}</td>
                        <td className="sm-table__right sm-table__strong">{spend}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="si-card sm-card sm-card--flush">
              <div className="sm-card__head">
                <p className="sm-card__title">Payment Optimization Levers</p>
                <span className="sm-tag">Working Capital</span>
              </div>
              <table className="sm-table">
                <thead>
                  <tr>
                    <th>Lever</th>
                    <th>Current</th>
                    <th>Target</th>
                    <th className="sm-table__right">Impact</th>
                    <th>Owner</th>
                  </tr>
                </thead>
                <tbody>
                  {LEVERS.map(({ lever, current, target, impact, owner }) => (
                    <tr key={lever}>
                      <td className="sm-table__strong">{lever}</td>
                      <td className="sm-table__accent">{current}</td>
                      <td className="sm-table__muted">{target}</td>
                      <td className="sm-table__right sm-table__accent sm-table__strong">{impact}</td>
                      <td className="sm-table__muted">{owner}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="sm-callout">
                <div className="sm-callout__body">
                  <p className="sm-callout__eyebrow">From: Payment &amp; Working Capital</p>
                  <p className="sm-callout__text">
                    Extending core terms from 38.4 to 45 days frees <strong>$1.32M working capital</strong>; segmenting by
                    risk preserves continuity while a 2/10 Net 30 program captures <strong>$0.42M</strong>.
                  </p>
                </div>
                <button type="button" className="sm-callout__btn">
                  Send to Opportunity
                  <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="si-card sm-empty">{activeTab} content is coming soon.</div>
        )}
      </section>
    </div>
  );
}
